import { Loader2, Plus, Wallet } from 'lucide-react';
import { formatMoney } from '@/shared/lib/format/money';
import type { ScreenListItem } from '@/features/campaigns/components/ScreensStep/ScreensStep.types';

type Suggestion = { item: ScreenListItem; plays: number; cost: number };

type UnusedBudgetPanelProps = {
  remaining: number;
  budget: number;
  /** The screens already chosen can take the leftover as extra plays. */
  canFill: boolean;
  onFill: () => void;
  /** What applying the recommendation would change, per screen. */
  recommendation: Array<{ key: string; name: string; from: number; plays: number; cost: number }>;
  /** Other screens with free plays, used when the chosen screens are full. */
  suggestions: Suggestion[];
  addingId: string | null;
  disabled: boolean;
  onAdd: (screen: ScreenListItem, plays: number) => void;
  /** The user confirmed they only want to spend what the plan costs. */
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
  /** What the plan costs now. */
  spending: number;
};

/** Shown while budget is left over: the user spends it before the pay button unlocks. */
export function UnusedBudgetPanel({
  remaining,
  budget,
  canFill,
  onFill,
  recommendation,
  suggestions,
  addingId,
  disabled,
  onAdd,
  accepted,
  onAcceptedChange,
  spending,
}: UnusedBudgetPanelProps) {
  return (
    <section
      className="camp-section border-amber-200 bg-amber-50 text-left"
      role="region"
      aria-label="Unused budget"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
          <Wallet className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="m-0 text-[15px] font-semibold text-amber-950">
            {formatMoney(remaining)} of your {formatMoney(budget)} budget is unused
          </h2>
          <p className="mt-1 mb-0 text-xs leading-relaxed text-amber-900">
            Use it to show your ad more often, or confirm below that you want to spend less. Pay &amp; Launch unlocks either way.
          </p>

          {canFill ? (
            <>
              <p className="mt-3 mb-2 text-xs font-semibold text-amber-950">We recommend:</p>
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {recommendation.map((row) => (
                  <li key={row.key} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-xs text-[var(--dash-ink)]">
                    <span className="min-w-0 truncate font-semibold">{row.name}</span>
                    <span className="shrink-0 text-[var(--dash-muted)]">
                      {row.from} → {row.plays} plays a day · +{formatMoney(row.cost)}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={onFill}
                disabled={disabled}
                className="dash-btn-gradient mt-3 inline-flex items-center rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                Apply recommendation
              </button>
              <span className="ml-3 text-xs text-amber-900">You can still change any play count afterwards.</span>
            </>
          ) : (
            <>
              <p className="mt-3 mb-2 text-xs font-semibold text-amber-950">
                Your screens have no more free plays. These screens do:
              </p>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {suggestions.map(({ item, plays, cost }) => {
                  const id = String(item._id ?? item.id ?? '');
                  const place = [item.address?.area, item.address?.city].filter(Boolean).join(', ');
                  return (
                    <li key={id} className="flex items-center gap-3 rounded-xl border border-amber-200 bg-white p-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="m-0 truncate text-sm font-bold text-[var(--dash-ink)]">{item.screenName || item.name}</p>
                        <p className="m-0 mt-0.5 truncate text-xs text-[var(--dash-muted)]">
                          {place ? `${place} · ` : ''}up to {plays} plays a day · about {formatMoney(cost)}
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={disabled || addingId === id}
                        onClick={() => onAdd(item, plays)}
                        className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-[var(--dash-brand)]/40 px-2.5 py-1.5 text-xs font-semibold text-[var(--dash-brand)] disabled:opacity-45"
                      >
                        {addingId === id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                        Add {plays} plays
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          <label className="mt-4 flex cursor-pointer items-start gap-2 text-xs leading-relaxed text-amber-950">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(event) => onAcceptedChange(event.target.checked)}
              disabled={disabled}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--dash-brand)]"
            />
            <span>
              I only want to spend {formatMoney(spending)} of my {formatMoney(budget)} budget. The rest stays unused.
            </span>
          </label>
        </div>
      </div>
    </section>
  );
}
